"use client";

import { motion } from "framer-motion";
import {
  ArrowRight,
  GraduationCap,
  Building2,
  CheckCircle2,
  ShieldCheck,
  Trophy,
  Banknote,
} from "lucide-react";

import GradientButton from "./ui/GradientButton";
import OutlineButton from "./ui/OutlineButton";

const benefits = [
  "Student registration",
  "Online competitive exams",
  "Performance insights",
  "Rankings where available",
  "Eligible competition rewards",
];

const journey = ["Take Exams", "Compete", "Review Performance", "Improve"];

export default function CTA() {
  return (
    <section className="relative overflow-hidden bg-[#050B1A] py-20 sm:py-24 lg:py-32">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute left-1/2 top-1/2 h-[42rem] w-[42rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-gold/10 blur-[130px]" />
        <div className="absolute -left-48 bottom-[-16rem] h-[34rem] w-[34rem] rounded-full bg-blue-500/10 blur-[120px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/40 to-transparent" />
        <div className="absolute inset-0 opacity-[0.025] [background-image:linear-gradient(rgba(255,255,255,0.8)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.8)_1px,transparent_1px)] [background-size:64px_64px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.045] px-5 py-10 text-center shadow-[0_30px_90px_rgba(0,0,0,0.22)] backdrop-blur-2xl sm:px-8 sm:py-12 lg:px-16 lg:py-16"
        >
          <div className="pointer-events-none absolute left-1/2 top-[-12rem] h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-brand-gold/10 blur-[100px]" />
          <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/60 to-transparent sm:inset-x-16" />

          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/25 bg-brand-gold/10 px-4 py-2 backdrop-blur-xl">
              <Trophy className="h-3.5 w-3.5 text-brand-gold" aria-hidden="true" />
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-brand-gold">
                Learn. Compete. Improve.
              </span>
            </div>

            <h2 className="mx-auto mt-7 max-w-4xl text-3xl font-black leading-[1.08] tracking-tight text-white sm:text-4xl md:text-5xl lg:text-[4rem]">
              Make Every Practice Session
              <br className="hidden sm:block" />
              <span className="text-brand-gold">Count.</span>
            </h2>

            <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-white/70 sm:text-lg sm:leading-8">
              Build consistency with online exams, review your performance, and
              challenge yourself through competitive learning on The Conclusion
              Daily.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: 0.1 }}
              className="mx-auto mt-8 flex max-w-2xl flex-col items-center justify-center gap-4 rounded-2xl border border-brand-gold/20 bg-brand-gold/[0.06] px-5 py-5 sm:flex-row sm:text-left"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-brand-gold/25 bg-brand-gold/10 text-brand-gold">
                <Banknote className="h-5 w-5" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-bold text-white">
                  Competition rewards may be available
                </p>
                <p className="mt-1 text-sm leading-6 text-white/65">
                  Rewards depend on the specific competition, eligibility, and
                  its published rules. Participation does not guarantee a payout.
                </p>
              </div>
              <Trophy className="hidden h-5 w-5 shrink-0 text-brand-gold/60 sm:ml-auto sm:block" aria-hidden="true" />
            </motion.div>

            <div className="mx-auto mt-7 grid max-w-3xl grid-cols-2 gap-3 sm:grid-cols-4">
              {journey.map((step, index) => (
                <div key={step} className="rounded-xl border border-white/[0.08] bg-white/[0.025] px-3 py-3">
                  <div className="mx-auto flex h-7 w-7 items-center justify-center rounded-lg bg-brand-gold/10 text-xs font-black text-brand-gold">
                    {index + 1}
                  </div>
                  <p className="mt-2 text-xs font-semibold text-white/75">{step}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <GradientButton href="/signup">
                <GraduationCap className="mr-2 h-5 w-5" aria-hidden="true" />
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </GradientButton>

              <OutlineButton href="/institutes">
                <Building2 className="mr-2 h-5 w-5" aria-hidden="true" />
                For Institutes
              </OutlineButton>
            </div>

            <div className="mx-auto mt-12 flex max-w-4xl flex-wrap justify-center gap-x-6 gap-y-4 border-t border-white/[0.08] pt-7 sm:gap-x-7">
              {benefits.map((item) => (
                <div key={item} className="flex items-center gap-2 text-left text-sm text-white/70">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-gold" aria-hidden="true" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-7 flex items-center justify-center gap-2 text-center"
        >
          <ShieldCheck className="h-4 w-4 shrink-0 text-brand-gold/80" aria-hidden="true" />
          <span className="text-xs font-medium leading-5 text-white/55">
            A competitive learning platform for students, educators, and institutes.
          </span>
        </motion.div>
      </div>
    </section>
  );
}
